import { useState, useCallback } from "react";
import { Bell, BellOff } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotifications } from "@/lib/hooks/use-notifications";
import { useAuth } from "@/lib/contexts/auth-context";
import type { Notification } from "@/lib/api/notifications";
import { fetchPendingInvitations } from "@/lib/api/collaborators";
import { getPendingIncomingTransfers } from "@/lib/api/transfers";
import { NotificationActions } from "./notification-actions";
import { NotificationIcon } from "./notification-icon";

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

interface NotificationBellProps {
  isScrolled: boolean;
}

export function NotificationBell({ isScrolled }: NotificationBellProps) {
  const { user } = useAuth();
  const { notifications, unreadCount, markAllRead, dismiss, clearAll } = useNotifications(
    user?.id ?? null
  );
  const [open, setOpen] = useState(false);
  const [pendingInviteIds, setPendingInviteIds] = useState<Set<string>>(new Set());
  const [pendingTransferIds, setPendingTransferIds] = useState<Set<string>>(new Set());
  // Snapshot of unread IDs at the moment the dropdown opens — keeps visual
  // state as "unread" for the duration of that dropdown session
  const [visuallyUnreadIds, setVisuallyUnreadIds] = useState<Set<string>>(new Set());

  // Fetch pending collab invites and transfers when dropdown opens
  const refreshPendingActions = useCallback(async () => {
    try {
      const [invites, transfers] = await Promise.all([
        fetchPendingInvitations().catch(() => []),
        getPendingIncomingTransfers().catch(() => []),
      ]);
      setPendingInviteIds(new Set(invites.map((inv) => inv.id)));
      setPendingTransferIds(new Set(transfers.map((t) => t.id)));
    } catch {
      // Non-critical
    }
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen) {
        // Snapshot which notifications are currently unread before marking them
        setVisuallyUnreadIds(
          new Set(notifications.filter((n) => !n.read).map((n) => n.id))
        );
        if (unreadCount > 0) markAllRead();
        refreshPendingActions();
      }
    },
    [unreadCount, markAllRead, refreshPendingActions, notifications]
  );

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative p-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
          aria-label="Notifications"
        >
          <Bell
            className={`h-[18px] w-[18px] transition-colors duration-300 ${
              isScrolled ? "text-on-dark" : "text-on-light"
            }`}
            strokeWidth={2}
          />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white px-0.5">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] !left-auto !right-0 p-0 max-h-[480px] overflow-hidden flex flex-col rounded-xl shadow-xl border-border/50"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <h3 className="text-sm font-semibold text-on-dark tracking-tight">
            Notifications
          </h3>
          {notifications.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              className="text-[11px] font-medium text-on-dark-muted hover:text-on-dark transition-colors"
            >
              CLEAR ALL
            </button>
          )}
        </div>

        {/* Notification list */}
        <div className="overflow-y-auto overscroll-contain">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center text-center px-4 pt-3 pb-8">
              <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center mb-3">
                <BellOff className="h-5 w-5 text-on-dark-muted-40" />
              </div>
              <p className="text-sm font-medium text-on-dark-muted">
                No notifications yet
              </p>
              <p className="text-xs text-on-dark-muted-40 mt-1">
                We'll let you know when something happens
              </p>
            </div>
          ) : (
            notifications.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                isUnread={visuallyUnreadIds.has(notification.id)}
                pendingInviteIds={pendingInviteIds}
                pendingTransferIds={pendingTransferIds}
                onDismiss={() => dismiss(notification.id)}
                onActionComplete={() => setOpen(false)}
              />
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationRow({
  notification,
  isUnread,
  pendingInviteIds,
  pendingTransferIds,
  onDismiss,
  onActionComplete,
}: {
  notification: Notification;
  isUnread: boolean;
  pendingInviteIds: Set<string>;
  pendingTransferIds: Set<string>;
  onDismiss: () => void;
  onActionComplete: () => void;
}) {

  return (
    <div className="group relative px-4 py-3">
      <div className="flex gap-3 items-start">
        {/* Type icon */}
        <NotificationIcon type={notification.type} isUnread={true} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[13px] leading-tight font-semibold text-on-dark">
              {notification.title}
            </p>
            {/* Timestamp / dismiss — CLEAR replaces timestamp on hover */}
            <div className="flex-shrink-0 w-[52px] text-right">
              <span className="text-[11px] text-on-dark-muted-50 whitespace-nowrap group-hover:hidden">
                {formatRelativeTime(notification.created_at)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss();
                }}
                className="text-[11px] font-medium text-on-dark-muted hover:text-on-dark hidden group-hover:inline transition-colors"
                aria-label="Dismiss"
              >
                CLEAR
              </button>
            </div>
          </div>
          <p className="text-[12px] leading-relaxed mt-0.5 line-clamp-2 text-on-dark-muted">
            {notification.message}
          </p>

          {/* Action buttons */}
          <NotificationActions
            notification={notification}
            pendingInviteIds={pendingInviteIds}
            pendingTransferIds={pendingTransferIds}
            onActionComplete={onActionComplete}
          />
        </div>

        {/* Unread indicator */}
        {isUnread && (
          <div className="flex-shrink-0 pt-1.5">
            <div className="w-2 h-2 rounded-full bg-primary" />
          </div>
        )}
      </div>
    </div>
  );
}
